import * as Phaser from 'phaser';
import { PaintedScene } from '../PaintedScene';
import { HearthFire, FIRE_CRACKLE_KEY, HEARTH_GLOW_TEXTURE_KEY } from '../HearthFire';
import { WalkerRig, WALKER_RIG_ATLAS, WALKER_RIG_ATLAS_PNG, WALKER_RIG_ATLAS_JSON } from '../WalkerRig';
import { ScenePropsSystem } from '../ScenePropsSystem';
import { GROVE_PROPS, FIRE_NX, FIRE_NY } from './oldGroveProps';
import { GroveScore } from '../../../audio/GroveScore';
import { NightMask } from '../NightMask';
import { NightVigil, VIGIL_SEED_FRACTION } from '../NightVigil';
import { GroveVisitor } from '../GroveVisitor';
import { endingLine, OutcomeRecorder } from '../vigilEnding';
import { GATHER_WORK_MS } from '../hearthRules';
import { TimeSystem } from '../../../systems/TimeSystem';
import { emitChromeMode } from '../../../game/HudState';

export const OLD_GROVE_SHRINE_PHASER_KEY = 'OldGroveShrineScene' as const;
export const OLD_GROVE_SHRINE_MANIFEST_KEY = 's-105-old-grove-shrine' as const;

/** The basin is wide; the 32px source sprite needs help to fill it. */
const FIRE_SCALE = 0.92;

/**
 * The manifest spawns the walker in the middle of the basin, which is exactly
 * where the fire goes — a 96px sprite sat on top of it. Standing them at the
 * basin's edge instead reads better anyway: someone beside their fire.
 */
const WALKER_SPAWN_NX = 0.20;
const WALKER_SPAWN_NY = 0.76;



/** How close a click has to land on a fuel prop, in px, to count as reaching for it. */
const GATHER_CLICK_SLOP = 26;

/** Click within this of the hearth, in normalised x, and the errand is "feed the fire". */
const FIRE_CLICK_RADIUS = 0.055;

/** Where the walker stands to work — beside a thing, never on top of it. */
const APPROACH_DY = 0.022;

/**
 * How long the "offered it, the fire would not take it" gesture runs.
 *
 * Long enough to read, short enough not to lock her out of another click. The
 * spec asks for feedback on every input within ~100ms; the lift begins on the
 * first frame after arrival.
 */
const OFFER_MS = 620;

/**
 * The lantern's own light, in px against the 960-wide viewport.
 *
 * Small next to the fire, which tops out near 365 — a lamp is not a bonfire,
 * and if it were there would be no reason to keep the hearth. It is exactly
 * enough to work by: stood beside the far deadfall it brings that deadfall into
 * reach, and nothing else.
 *
 * It does not vary. That is the point of a lamp, and it is what makes it worth
 * carrying into a part of the grove the fire's own light cannot hold.
 */
const LANTERN_RADIUS_FRAC = 0.17;

/** A lamp gutters far less than an open fire. */
const LANTERN_FLICKER = 0.25;

/**
 * How far up the lantern its flame sits, as a fraction of its height.
 *
 * Props are anchored at the base, so lighting from `img.y` put the pool at the
 * lantern's *foot* — measured, 38px below the lamp housing on a 54px-tall
 * sprite, which reads as light welling up out of the ground beside it rather
 * than shining from it.
 */
const LANTERN_FLAME_HEIGHT = 0.7;

/**
 * Carried, the lantern is drawn at this fraction of the walker's width.
 *
 * It was 0.5, which on a 110px walker made a 55x72 object hung on her chest —
 * it did not read as carrying a lamp, it read as the lamp having eaten her.
 * At 0.30 it is a thing in her arms and she is still the subject of the frame.
 */
const CARRIED_LANTERN_WIDTH = 0.30;

const PROP_FILES: Record<string, string> = {
  'prop-fire-ring': 'assets/scenes/verdana/S-105/props/fire-ring.png',
  'prop-lantern': 'assets/scenes/verdana/S-105/props/stone-lantern.png',
  'prop-fallen-log': 'assets/scenes/verdana/S-105/props/fallen-log.png',
  'prop-stump': 'assets/scenes/verdana/S-105/props/stump.png',
  'prop-kindling': 'assets/scenes/verdana/S-105/props/kindling.png',
  'prop-fern': 'assets/scenes/verdana/S-105/props/fern.png',
  'prop-grass': 'assets/scenes/verdana/S-105/props/grass.png',
  'prop-stones': 'assets/scenes/verdana/S-105/props/stones.png',
};

/** What the walker is currently on her way to do. */
type Errand =
  | { kind: 'none' }
  | { kind: 'gather'; id: number; nx: number; ny: number; key: string }
  | { kind: 'lantern'; nx: number; ny: number }
  | { kind: 'feed' };

/**
 * What is in her hands. One thing at a time, always — the lantern is stone and
 * takes both, which is the only hard trade in the game: take the light out and
 * you come back empty, or take the wood and walk home in the dark.
 */
type Carried =
  | { kind: 'fuel'; key: string; fuel: number }
  | { kind: 'lantern' };

/**
 * The Old Grove Shrine — the single scene of The Small Hours.
 *
 * See docs/design/single-scene-game-spec.md. The fire in the basin is the
 * whole game: the only obvious mechanic is tending it, its light is what the
 * player can see and reach by, and the night it has to last is a real one.
 */
export class OldGroveShrineScene extends PaintedScene {
  private hearth: HearthFire | null = null;
  private lightCamera: Phaser.Cameras.Scene2D.Camera | null = null;
  private rig: WalkerRig | null = null;
  private props: ScenePropsSystem | null = null;
  private score: GroveScore | null = null;
  private nightMask: NightMask | null = null;
  private vigil: NightVigil | null = null;
  /** 0 = full day, 1 = deep night. Driven by the time system's ambient. */
  private darkness = 0;
  private rigPrev: { x: number; y: number } | null = null;

  private errand: Errand = { kind: 'none' };
  private carrying: Carried | null = null;
  private carrySprite: Phaser.GameObjects.Image | null = null;
  /**
   * 0..1 through the "offered it and the fire did not take it" gesture.
   *
   * A refused feed used to change nothing at all: `flare` returns before it
   * touches the flare timer, the embers or the fuel, there is no feed sound in
   * the game, and she was already visibly holding the wood for the whole walk
   * over. So the click produced no perceptible delta of any kind — not "a
   * legible piece of nothing-happened", just nothing, indistinguishable from
   * the input being dropped. This is the honest cue: she lifts it toward the
   * fire and lowers it again. It claims nothing was delivered, because nothing
   * was, and it says the click was heard.
   */
  private offerT = 0;
  /**
   * The armful she is currently working loose, and how far in she is.
   *
   * Extraction is the only thing in the game that costs, so this is the only
   * place the player is ever made to wait. Walking away cancels it and costs
   * nothing — an abandoned gather must be free, or the game starts taxing
   * changing your mind.
   */
  private working: { id: number; key: string; nx: number; ny: number; elapsed: number } | null = null;
  /** Index of the lantern in the props system, or -1 if its art is missing. */
  private lanternId = -1;
  /** The lantern's own glow, so it reads as a light and not a lit patch. */
  private lanternGlow: Phaser.GameObjects.Image | null = null;
  /** Something at the treeline, drawn to a fire that has been kept. */
  private visitor: GroveVisitor | null = null;
  /** What the run has consisted of, accumulated as it happens. */
  private readonly outcome = new OutcomeRecorder();
  private endingText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super(OLD_GROVE_SHRINE_PHASER_KEY, OLD_GROVE_SHRINE_MANIFEST_KEY);
  }

  /**
   * The vigil opens at dusk. Every other painted scene seeds its clock from
   * wall time, which for a game that *is* one night would mean most players
   * arriving at noon and waiting.
   */
  protected dayNightSeedFraction(): number | null {
    return VIGIL_SEED_FRACTION;
  }

  preload(): void {
    super.preload();
    GroveScore.preload(this);

    if (!this.textures.exists(WALKER_RIG_ATLAS)) {
      this.load.atlas(WALKER_RIG_ATLAS, WALKER_RIG_ATLAS_PNG, WALKER_RIG_ATLAS_JSON);
    }

    for (const [key, path] of Object.entries(PROP_FILES)) {
      if (!this.textures.exists(key)) this.load.image(key, path);
    }

    if (!this.cache.audio.exists(FIRE_CRACKLE_KEY)) {
      this.load.audio(FIRE_CRACKLE_KEY, [
        'audio/sfx/fire-crackle-loop.ogg',
        'audio/sfx/fire-crackle-loop.mp3',
      ]);
    }
  }

  protected onSceneReady(): void {
    super.onSceneReady();

    // First, before anything that could throw. The scene is the interface:
    // everything the console was reporting is either read off the painting
    // already or belongs to a different game — the flame is the fire's health,
    // the sky is the clock, what she is holding is what she carries, and
    // vitality, breath and four ability sockets are v1 RPG furniture over a
    // game with none of those things.
    //
    // Ordering is not cosmetic. Mounted last, a throw anywhere in the setup
    // below would leave the wrong HUD sitting over a half-built scene.
    // `onSceneShutdown` hands it back at the top for the same reason.
    //
    // Knowingly given up with it: LetterboxUi opens only by clicking a socket
    // in the bottom band, so while the vigil is running there is no way to read
    // a letter. Arrivals still announce themselves through SlipToasts, which
    // live over the painting and are unaffected, and one night is short enough
    // that a delivery is unlikely to land inside it. If letters ever matter
    // here they need an in-world way in, not a socket back.
    emitChromeMode(this.game, 'bare');

    this.walkerNX = WALKER_SPAWN_NX;
    this.walkerNY = WALKER_SPAWN_NY;

    this.hearth = new HearthFire(this, {
      nx: FIRE_NX,
      ny: FIRE_NY,
      scale: FIRE_SCALE,
      worldWidth: () => this.worldWidth(),
    });
    this.mountRig();
    if (this.assetSpec) {
      this.props = new ScenePropsSystem(this, this.assetSpec, GROVE_PROPS);
      this.lanternId = this.props.idOf('prop-lantern');
    }
    this.mountLanternGlow();
    // Before the light camera, so it is in the set that escapes the grade.
    this.visitor = new GroveVisitor(this, {
      firePosition: () => this.hearth?.getPosition() ?? { x: 0, y: 0 },
      lightRadius: () => this.activeLights()[0]?.radius ?? 190,
      // Per run, or it waits under the same tree for every player forever —
      // which quietly cancels the one thing in the game that is supposed to
      // differ between nights.
      seed: (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0,
    });
    this.mountLightCamera();
    // The region's single looping track is wrong for a game that stays in one
    // clearing all night; the grove scores itself off the fire instead.
    this.score = new GroveScore(this);

    // Above the near foreground so the trunks and ferns fall into shadow too —
    // if the darkness sat under them they would stay lit at the frame's edge
    // and give the whole effect away.
    this.nightMask = new NightMask(this, 400);

    // The run itself. Advanced by the same delta as the scene's TimeSystem, so
    // the grade and the vigil describe one night rather than two.
    this.vigil = new NightVigil();
    this.vigil.onDawn = () => this.onDawn();
    this.vigil.onComplete = () => this.onVigilOver();

    this.events.on(TimeSystem.EVENTS.TICK, this.onTimeForDarkness, this);
    this.scale.on('resize', this.onResize, this);
  }

  /**
   * First light. The one moment in the game that is not the player's doing —
   * they kept the fire, and the morning came anyway, which is the point.
   *
   * `grove-dawn` has existed since the score was written and has never played,
   * because until now there was no dawn to play it at.
   */
  private onDawn(): void {
    this.score?.playDawn();
    // The fire's story is told by the night. From here on nothing burns fuel
    // and the sky is doing the work, so the record closes: anything fed, found
    // or carried during the morning would be rewriting an ending it had no
    // part in.
    this.outcome.seal(this.hearth?.getFuel() ?? 0);
  }

  /**
   * The night is over. Stop the sky, and say what it came to.
   *
   * Freezing the clock is the load-bearing half: without it the morning the
   * player arrived at rolls on into afternoon and then a second dusk, and the
   * ending is erased by the world simply continuing. A run has to be allowed
   * to finish.
   */
  private onVigilOver(): void {
    this.dayNightFrozen = true;
    // Belt and braces, and honestly unreachable today: `seal` is idempotent, and
    // skipping dawn entirely would need one frame of ~180 s, which Phaser's
    // delta smoothing cannot deliver — `smoothDelta` discards any raw delta
    // over `1000 / fps.min` = 50ms and substitutes a clamped historical value
    // (config at src/config/game.config.ts). Kept because a future change to
    // that config, or to how the vigil is driven, would make it live again, and
    // an unsealed outcome is the bug this whole class exists to prevent.
    this.outcome.seal(this.hearth?.getFuel() ?? 0);

    const line = endingLine(this.outcome.get());
    // Dark ink with a soft light halo, not cream. The ending always lands in
    // full morning, and cream on a sunlit forest floor measured a contrast
    // ratio of 1.71 against a readable floor of 3 — the line was on screen and
    // could not be read. Ink takes it to 2.55: still short of the floor by the
    // numbers, but that ratio samples a scanline and the glow lifts every
    // antialiased edge, so the glyph cores read cleanly. Verified by eye, and
    // the halo is what carries it back if an ending ever lands somewhere dark.
    //
    // Both figures use the same method — the decile of the text band furthest
    // from the ground mean. An earlier note said 1.22, which came from
    // measuring the DARKEST pixels of light-on-dark text and so sampled the
    // ground between the letters rather than the letters.
    const t = this.add.text(this.scale.width / 2, this.scale.height * 0.82, line, {
      fontFamily: "'Almendra', serif",
      fontSize: '22px',
      color: '#2e2118',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#f6ecd8', blur: 7, fill: true },
    });
    t.setOrigin(0.5, 0.5);
    t.setScrollFactor(0);
    t.setDepth(600);
    t.setAlpha(0);
    // Slow. The last thing the player reads should arrive at the pace the rest
    // of the scene moves at, not at the pace of a results screen.
    this.tweens.add({ targets: t, alpha: 1, duration: 3200, ease: 'Sine.easeInOut' });
    this.endingText = t;
  }

  /**
   * The lantern's glow.
   *
   * Measured, the night mask alone lifted the ground around a carried lantern
   * from luminance 40 to 51 — real, but on a base that dark it reads as a
   * slightly less black patch rather than as a lamp. The fire looks hot for a
   * reason the lantern did not share: it has an ADD-blended glow of its own,
   * drawn on the unfiltered camera so the night grade cannot dim the one thing
   * that is supposed to be bright. This is that, at a lamp's scale.
   */
  private mountLanternGlow(): void {
    if (this.lanternId < 0 || !this.textures.exists(HEARTH_GLOW_TEXTURE_KEY)) return;
    const g = this.add.image(0, 0, HEARTH_GLOW_TEXTURE_KEY);
    g.setBlendMode(Phaser.BlendModes.ADD);
    g.setOrigin(0.5, 0.5);
    // Under the walker, so carrying it lights the ground at her feet rather
    // than washing out her face.
    g.setDepth(95);
    this.lanternGlow = g;
  }

  /** Keep the glow on the lantern, wherever the lantern has got to. */
  private updateLanternGlow(): void {
    const g = this.lanternGlow;
    if (!g) return;
    const at = this.lanternPosition();
    if (!at) { g.setVisible(false); return; }
    g.setVisible(true);
    g.setPosition(at.x, at.y);
    // Sized off the same radius the mask and the reach check use, so the thing
    // you can see is the thing that is lighting the grove.
    const radius = LANTERN_RADIUS_FRAC * this.scale.width;
    g.setScale((radius * 1.5) / (g.width || 256));
    g.setAlpha(0.30 + 0.16 * this.darkness);
  }

  /**
   * The fire renders through its own camera, which carries no filters.
   *
   * `TimeOfDayColorGrader` attaches a ColorMatrix to the main camera, and the
   * night preset multiplies brightness by 0.28. That is right for the grove and
   * badly wrong for the fire: it dimmed the single light source by the same
   * amount as the darkness it is supposed to push back, so a roaring fire read
   * as a faint smear. Splitting it onto an unfiltered camera lets the grove go
   * properly dark while the fire stays hot — which is the whole premise of the
   * scene, and load-bearing for Phase 2, where the glow defines reach.
   */
  private mountLightCamera(): void {
    const fireObjects = this.lightObjects();
    if (fireObjects.length === 0) return;

    this.cameras.main.ignore(fireObjects);

    const rigContainer = this.rig?.getContainer();

    const cam = this.cameras.add(0, 0, this.scale.width, this.scale.height);
    cam.transparent = true;
    // It draws, it does not listen. With this camera in the input set it wins
    // `pointer.camera` over the main one (verified in the running game), which
    // makes every click's projection depend on undocumented resolution order
    // between two cameras. Measured, `worldX` still came back correct, so this
    // is not a bug being fixed — it is a coin-flip being removed.
    cam.inputEnabled = false;
    // Everything that is not the fire belongs to the graded camera.
    cam.ignore(this.children.list.filter((o) => !fireObjects.includes(o)));
    // Explicitly, because a Container's children never appear in children.list
    // and so were never covered by the filter above — the light camera was
    // drawing the rig a second time, un-graded, over the top of itself.
    if (rigContainer) cam.ignore(rigContainer);

    // It has to travel with the main camera or the fire burns beside its own
    // basin — measured at 67px adrift the moment the camera left the origin,
    // and growing with every step east. Copying `scrollX` in update() gets
    // within a few pixels but always lags a frame, because Phaser moves its
    // cameras after the scene has updated; giving this one the same follow
    // target and the same lerp makes the two compute the identical scroll in
    // the same pass instead.
    const main = this.cameras.main;
    cam.setBounds(0, 0, this.worldWidth(), this.scale.height);
    cam.setScroll(main.scrollX, main.scrollY);
    if (this.walker && main.deadzone) {
      cam.startFollow(this.walker, true, 0.08, 0.08);
      cam.setDeadzone(main.deadzone.width, main.deadzone.height);
    }

    this.lightCamera = cam;

    // Objects added later (walker animations, overlays, debug) must also be
    // kept off the light camera, or they would render ungraded on top.
    this.events.on(Phaser.Scenes.Events.ADDED_TO_SCENE, this.onChildAdded, this);
  }

  /**
   * Swap the generated walk sheets for the code cutout rig.
   *
   * `waker-walk-*.png` are six frames generated independently with no skeleton
   * conditioning and no identity anchor, so the character morphs frame to
   * frame. The rig was solved on 2026-08-10 (3faa3003) for the hand-rolled
   * game and never crossed into this client — see docs/AI-ANIMATION-PIPELINE.md,
   * whose thesis is that AI generates appearance and must never own timing.
   */
  private mountRig(): void {
    if (!this.walker) return;
    this.walker.setVisible(false);
    this.walkerShadow?.setVisible(false);

    this.rig = new WalkerRig(this, this.walker.x, this.walker.y, this.walker.depth);
    // The rig fires twice per gait cycle, on each contact, so the sound lands
    // with the foot instead of on a 350ms wall clock.
    this.footstepsDrivenByRig = true;
    this.rig.onFootfall = () => this.triggerFootstep();
    this.rigPrev = { x: this.walker.x, y: this.walker.y };
  }

  private updateRig(delta: number): void {
    if (!this.rig || !this.walker) return;
    const x = this.walker.x;
    const y = this.walker.y;
    const scale = Math.abs(this.walker.scaleX) || 1;

    const prev = this.rigPrev ?? { x, y };
    // Distance actually travelled, converted from screen px into the rig's own
    // source space. Stride lock needs real distance, not intended distance.
    const stepScreen = Math.hypot(x - prev.x, y - prev.y);
    const stepSource = stepScreen / Math.max(scale, 0.01);
    const dirX = x - prev.x;
    this.rigPrev = { x, y };

    this.rig.setPosition(x, y);
    this.rig.setScale(scale);
    this.rig.setDepth(this.walker.depth);
    this.rig.update(delta, stepSource, dirX);
  }

  /**
   * Ambient intensity runs 1 at noon down to 0.25 at night.
   *
   * Dividing by the full 0.75 range meant dusk — which already looks like night
   * on screen, because the colour grader is well into its night curve — only
   * produced 0.6 darkness, so the mask sat at half strength and the whole
   * effect read as a faint vignette. Reaching full dark by mid-dusk matches
   * what the grade is already doing.
   */
  private onTimeForDarkness(payload: { ambient: { intensity: number } }): void {
    const intensity = payload?.ambient?.intensity ?? 1;
    this.darkness = Phaser.Math.Clamp((1 - intensity) / 0.55, 0, 1);
    this.dimAmbientOverlay();
  }

  /**
   * Pull the ambient overlay down at night.
   *
   * AmbientOverlayScene is a separate scene rendering ABOVE this one, so its
   * drifting motes are untouched by the night mask — they stayed equally bright
   * in the pitch-black corners as beside the fire, which flatly contradicts
   * "the fire is the only light". Dimming its camera costs nothing and needs no
   * knowledge of its internals.
   */
  private dimAmbientOverlay(): void {
    const overlay = this.scene.get('AmbientOverlayScene');
    const cam = overlay?.cameras?.main;
    if (cam) cam.setAlpha(1 - this.darkness * 0.88);
  }

  /**
   * Every light in the grove right now, in WORLD space.
   *
   * The hearth, always. The lantern, wherever it happens to be standing — or
   * riding in her hands. The mask and the props both read this one list, so
   * what is lit and what can be reached can never disagree.
   *
   * Not `lights()`: Phaser.Scene already owns that name for its Lights
   * Manager, and shadowing it quietly stops the scene being assignable to
   * `Scene` anywhere it is passed as one.
   */
  private activeLights(): Array<{ x: number; y: number; radius: number; warmth: number; flicker: number }> {
    const out: Array<{ x: number; y: number; radius: number; warmth: number; flicker: number }> = [];

    if (this.hearth) {
      const pos = this.hearth.getPosition();
      const fuel = this.hearth.getFuel();
      // NOT getLightRadius() * worldWidth(): that is a normalised reach, and
      // against a world two viewports wide it comes out at ~714px — wider than
      // the screen, so the hole swallowed the frame and only the corners
      // darkened. What the player sees is a screen-sized pool, so it is
      // measured against the viewport.
      // Floored: below about 190px the pool stops covering the character
      // sitting at the hearth, and a dying fire is exactly the moment the
      // player most needs to read her. Mood must not cost legibility.
      out.push({
        x: pos.x,
        y: pos.y,
        radius: Math.max(190, (0.12 + 0.26 * fuel) * this.scale.width),
        warmth: fuel,
        flicker: 1,
      });
    }

    const lantern = this.lanternPosition();
    if (lantern) {
      out.push({
        x: lantern.x,
        y: lantern.y,
        radius: LANTERN_RADIUS_FRAC * this.scale.width,
        warmth: 0.8,
        flicker: LANTERN_FLICKER,
      });
    }

    return out;
  }

  /** Where the lantern is throwing light from — her hands, or the ground. */
  private lanternPosition(): { x: number; y: number } | null {
    if (this.lanternId < 0) return null;
    if (this.carrying?.kind === 'lantern') {
      // Held about chest height, so the pool sits on her rather than at her
      // feet and travels with her stride.
      const w = this.walker;
      if (!w) return null;
      return { x: w.x, y: w.y - w.displayHeight * 0.35 };
    }
    // Standing: the light comes from the lamp housing, not the plinth.
    const at = this.props?.worldPositionOf(this.lanternId);
    const h = this.props?.displayHeightOf(this.lanternId) ?? 0;
    return at ? { x: at.x, y: at.y - h * LANTERN_FLAME_HEIGHT } : null;
  }

  private updateNightMask(delta: number): void {
    if (!this.nightMask) return;
    const cam = this.cameras.main;
    const lights = this.activeLights();

    // World -> screen, because the mask is pinned to the viewport.
    this.nightMask.update(
      this.darkness,
      lights.map(l => ({ ...l, x: l.x - cam.scrollX, y: l.y - cam.scrollY })),
      delta,
    );

    // The props are lit in WORLD space — they travel with the scene, unlike the
    // screen-pinned mask. Shaded a little beyond the visible pool, so an object
    // at its edge is half-lit rather than switching on as you approach; and
    // reachable a good deal beyond *that*, because you can make out a shape at
    // the treeline long before the light is doing anything flattering to it.
    //
    // 2.4x is what puts the stump at nx 0.615 inside the fire's reach at a
    // strong fire and outside it at a middling one — the Phase 2 gate — and
    // what makes the lantern, stood beside the far deadfall, enough to work by.
    this.props?.updateLighting(
      lights.map(l => ({ x: l.x, y: l.y, tintRadius: l.radius * 1.35, reachRadius: l.radius * 2.4 })),
      this.darkness,
    );
  }

  // ── the loop: gather, carry, feed ─────────────────────────────────────────

  /**
   * Every click is one of three things: feed the fire, take an armful of
   * something, or walk. There is no verb menu and no inventory — what you can
   * do is a function of where you click and what you are already holding,
   * which is the whole of the interface.
   */
  protected onSceneClick(pointer: Phaser.Input.Pointer): boolean {
    if (!this.props || !this.hearth) return false;
    const w = this.worldWidth();
    const h = this.scale.height;
    const world = this.pointerWorld(pointer);
    const nx = world.x / w;
    const ny = world.y / h;

    // Clicking the hearth while holding something: go and put it down there.
    // Firewood goes on the fire; the lantern gets stood beside it, which is
    // also the only way to bring the light home without an extra verb.
    if (this.carrying && Math.hypot(nx - FIRE_NX, (ny - FIRE_NY) * 1.7) < FIRE_CLICK_RADIUS) {
      if (this.walkTo(...this.approachToFire())) {
        this.errand = { kind: 'feed' };
        this.cancelWork();
        return true;
      }
    }

    // Clicking something that burns. Empty-handed she just picks it up; with
    // the lantern in her hands she sets it down first, in one movement, and
    // that is how the light ends up exactly where it is useful. No second verb
    // and no key to discover — the click already says what she means.
    if (!this.carrying || this.carrying.kind === 'lantern') {
      const target = this.props.findGatherable(
        world.x, world.y, GATHER_CLICK_SLOP, this.carrying?.kind === 'lantern',
      );
      if (target) {
        const approach = this.approachTo(target.nx, target.ny);
        if (this.walkTo(approach[0], approach[1])) {
          this.errand = { kind: 'gather', id: target.id, nx: target.nx, ny: target.ny, key: target.key };
          this.cancelWork();
          return true;
        }
      }
    }

    // Clicking the lantern, empty-handed: go and pick it up.
    if (!this.carrying && this.lanternId >= 0 && this.hitsLantern(world.x, world.y)) {
      const at = this.props.positionOf(this.lanternId);
      if (at) {
        const approach = this.approachTo(at.nx, at.ny);
        if (this.walkTo(approach[0], approach[1])) {
          this.errand = { kind: 'lantern', nx: at.nx, ny: at.ny };
          this.cancelWork();
          return true;
        }
      }
    }

    // Anything else is just walking, and cancels whatever she was going to do.
    this.errand = { kind: 'none' };
    this.cancelWork();
    return false;
  }

  /** Did that click land on the lantern? Bounds, so its post counts too. */
  private hitsLantern(worldX: number, worldY: number): boolean {
    const b = this.props?.boundsOf(this.lanternId);
    if (!b) return false;
    const dx = Math.max(b.left - worldX, 0, worldX - b.right);
    const dy = Math.max(b.top - worldY, 0, worldY - b.bottom);
    return Math.hypot(dx, dy) <= GATHER_CLICK_SLOP;
  }

  /** A spot inside the walkable polygon, just in front of something. */
  private approachTo(nx: number, ny: number): [number, number] {
    const p = this.polygon?.closestPointInside(nx, ny + APPROACH_DY);
    return p ? [p.x, p.y] : [nx, ny + APPROACH_DY];
  }

  /** Stand on the side of the hearth she is already on, not through it. */
  private approachToFire(): [number, number] {
    const side = this.walkerNX < FIRE_NX ? -1 : 1;
    return this.approachTo(FIRE_NX + side * 0.05, FIRE_NY - APPROACH_DY);
  }

  /**
   * Resolve an errand on arrival.
   *
   * Arrival is "the path ran out AND she is actually near the thing" — the
   * path alone is not enough, because a keyboard nudge clears the path too and
   * would otherwise teleport an armful of wood into her hands from across the
   * clearing.
   */
  private tickErrand(): void {
    if (this.errand.kind === 'none' || this.isWalking()) return;

    if (this.errand.kind === 'gather') {
      const { id, nx, ny, key } = this.errand;
      this.errand = { kind: 'none' };
      if (Math.hypot(this.walkerNX - nx, (this.walkerNY - ny) * 1.7) > 0.09) return;
      // She needs both hands. Setting the lantern down here is what makes the
      // far deadfall gatherable at all, and it is why the light stays out in
      // the grove afterwards instead of following her home.
      if (this.carrying?.kind === 'lantern') this.setLanternDown();
      // Re-checked here rather than trusted from the click: the fire has been
      // burning down the whole walk, and something at the edge of the light
      // when she set off may be outside it by the time she arrives.
      if (!this.props?.canGatherNow(id)) return;
      // Arriving is not taking. Breaking an armful loose takes real seconds,
      // and the fire burns through every one of them.
      this.working = { id, key, nx, ny, elapsed: 0 };
      this.props.startWorking(id);
      return;
    }

    if (this.errand.kind === 'lantern') {
      const { nx, ny } = this.errand;
      this.errand = { kind: 'none' };
      if (this.carrying) return;
      if (Math.hypot(this.walkerNX - nx, (this.walkerNY - ny) * 1.7) > 0.10) return;
      this.props?.setPropVisible(this.lanternId, false);
      this.outcome.noteLanternCarried();
      this.pickUp({ kind: 'lantern' });
      return;
    }

    // feed
    this.errand = { kind: 'none' };
    if (!this.carrying || !this.hearth) return;
    if (Math.hypot(this.walkerNX - FIRE_NX, (this.walkerNY - FIRE_NY) * 1.7) > 0.10) return;
    if (this.carrying.kind === 'lantern') {
      this.setLanternDown();
      return;
    }
    // She keeps it if the fire has no room. Walking a log over to a roaring
    // fire and walking back still holding it is a small, legible piece of
    // nothing-happened; the alternative was the fire swallowing four fifths of
    // it and saying so nowhere.
    if (this.hearth.flare(this.carrying.fuel)) this.dropCarried();
    else this.offerT = 1;
  }

  /**
   * Work the current armful loose. The pile rocks; nothing else says so.
   *
   * Re-checked every frame rather than once on arrival: the fire has been
   * burning the whole time, and if the light pulls back off this pile while
   * she is working it, she stops. That is what makes letting the fire fall
   * while you are out there cost something.
   */
  private tickWork(delta: number): void {
    const w = this.working;
    if (!w) return;

    // Walked off with the keyboard, which never goes through onSceneClick and
    // so cannot cancel there. Standing somewhere else while a pile across the
    // clearing rocks itself apart would be a fine bug to ship.
    if (Math.hypot(this.walkerNX - w.nx, (this.walkerNY - w.ny) * 1.7) > 0.11) {
      this.cancelWork();
      return;
    }

    // The light can leave while she works. When it does the pile stops moving
    // *at once* rather than at the end of a wasted beat — the rocking going
    // still is the feedback, and a silent failure two seconds later would be
    // the worst of both.
    if (!this.props?.canGatherNow(w.id)) {
      this.cancelWork();
      return;
    }

    w.elapsed += delta;
    if (w.elapsed < GATHER_WORK_MS) return;

    this.working = null;
    this.props.stopWorking(w.id);
    const fuel = this.props.take(w.id);
    if (fuel > 0) this.pickUp({ kind: 'fuel', key: w.key, fuel });
  }

  /** Called away mid-gather. Costs nothing — changing your mind is free. */
  private cancelWork(): void {
    if (!this.working) return;
    this.props?.stopWorking(this.working.id);
    this.working = null;
  }

  /** Stand the lantern where she is. It lights that spot until she comes back. */
  private setLanternDown(): void {
    if (this.lanternId < 0) return;
    this.props?.moveProp(this.lanternId, this.walkerNX, this.walkerNY);
    this.props?.setPropVisible(this.lanternId, true);
    this.dropCarried();
  }

  /** Show her holding it. "What you carry" is read off the character, not a slot. */
  private pickUp(what: Carried): void {
    this.carrying = what;
    this.carrySprite?.destroy();
    const img = this.add.image(0, 0, what.kind === 'lantern' ? 'prop-lantern' : what.key);
    img.setOrigin(0.5, 0.5);
    this.carrySprite = img;
    this.layoutCarried();
  }

  private dropCarried(): void {
    this.carrying = null;
    this.carrySprite?.destroy();
    this.carrySprite = null;
  }

  /** Ease the offer gesture out. One rise and fall, then still again. */
  private tickOffer(delta: number): void {
    if (this.offerT <= 0) return;
    this.offerT = Math.max(0, this.offerT - delta / OFFER_MS);
  }

  private layoutCarried(): void {
    const img = this.carrySprite;
    if (!img || !this.walker) return;
    // Sized against the walker rather than the scene, so an armful reads as an
    // armful whether she is at the front of the clearing or the back.
    const isLantern = this.carrying?.kind === 'lantern';
    const width = this.walker.displayWidth * (isLantern ? CARRIED_LANTERN_WIDTH : 0.62);
    img.setScale(width / img.width);
    // Held out to the side she is facing, so she stays the subject of the
    // frame instead of standing behind her own luggage.
    const facing = this.rig?.isFacingLeft() ? -1 : 1;
    // A single rise and fall — sin over the whole window, so it leaves and
    // returns to exactly where it rests with no snap at either end.
    const offer = Math.sin((1 - this.offerT) * Math.PI);
    img.setPosition(
      this.walker.x + (isLantern ? facing * this.walker.displayWidth * 0.22 : 0)
        + facing * offer * this.walker.displayWidth * 0.10,
      this.walker.y - this.walker.displayHeight * (isLantern ? 0.30 : 0.42)
        - offer * this.walker.displayHeight * 0.16,
    );
    img.setRotation(facing * offer * 0.22);
    img.setDepth(this.walker.depth + 1);
    img.setFlipX(facing < 0);
  }

  /** Everything that is a light source, and so must escape the night grade. */
  private lightObjects(): Phaser.GameObjects.GameObject[] {
    const out = [...(this.hearth?.getDisplayObjects() ?? [])];
    if (this.lanternGlow) out.push(this.lanternGlow);
    if (this.visitor) out.push(...this.visitor.getDisplayObjects());
    return out;
  }

  private onChildAdded(obj: Phaser.GameObjects.GameObject): void {
    if (this.lightCamera && !this.lightObjects().includes(obj)) {
      this.lightCamera.ignore(obj);
    }
  }

  update(time: number, delta: number): void {
    super.update(time, delta);

    // Before the vigil, which can end on this very frame and freeze the
    // outcome. Read after it, a visitor arriving on the final frame of the
    // night would be missed by the line that exists to report it.
    if (this.hearth) this.visitor?.update(this.hearth.getFuel(), this.darkness, delta);
    const stage = this.visitor?.getStage();
    if (stage) this.outcome.noteVisitor(stage);

    this.vigil?.update(delta);
    // The fire only burns while there is a night to burn against. Once the sky
    // is doing the work it stops being a resource and goes back to being a
    // fire, which is the difference between an ending and a timer running out.
    if (this.vigil && this.vigil.getDarkRemaining() > 0) this.hearth?.burn(delta);
    this.hearth?.update(delta);
    this.updateLanternGlow();
    this.updateNightMask(delta);
    this.updateRig(delta);
    this.tickErrand();
    this.tickWork(delta);
    this.tickOffer(delta);
    this.layoutCarried();
    if (this.hearth) this.score?.update(this.hearth.getFuel(), delta);
  }

  protected onSceneShutdown(): void {
    this.events.off(Phaser.Scenes.Events.ADDED_TO_SCENE, this.onChildAdded, this);
    if (this.lightCamera) {
      this.cameras.remove(this.lightCamera);
      this.lightCamera = null;
    }
    this.scale.off('resize', this.onResize, this);
    this.events.off(TimeSystem.EVENTS.TICK, this.onTimeForDarkness, this);
    // Hand the console back — every other scene in the project still wants it.
    emitChromeMode(this.game, 'full');
    this.dropCarried();
    this.cancelWork();
    this.errand = { kind: 'none' };
    this.vigil = null;
    this.lanternGlow?.destroy();
    this.lanternGlow = null;
    this.visitor?.destroy();
    this.visitor = null;
    this.endingText?.destroy();
    this.endingText = null;
    this.dayNightFrozen = false;
    // The grove is not a dead end — the manifest wires an edge zone out to the
    // hamlet and three scenes wire back in, and re-entry re-runs onSceneReady
    // on the same long-lived instance. Without this, a second vigil inherits
    // the first one's facts and the closing line claims you were not alone on
    // a night nothing came to. An ending that lies is worse than no ending.
    this.outcome.reset();
    this.nightMask?.destroy();
    this.nightMask = null;
    this.score?.destroy();
    this.score = null;
    this.props?.destroy();
    this.props = null;
    this.rig?.destroy();
    this.rig = null;
    this.hearth?.destroy();
    this.hearth = null;
    super.onSceneShutdown();
  }

  /** Exposed for tuning and for the Phase 2 burn-down loop. */
  getHearth(): HearthFire | null {
    return this.hearth;
  }

  /** Exposed so the Phase 2 dawn beat can cue the closing track. */
  getScore(): GroveScore | null {
    return this.score;
  }

  private onResize(): void {
    this.nightMask?.resize();
    this.props?.layout();
    this.hearth?.layout();
    this.lightCamera?.setSize(this.scale.width, this.scale.height);
  }
}
